import type { FormEvent, ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { maskCpf } from '@/components/CpfCnpjInput/utils'
import {
  getAccountFlowContent,
  getAccountPrimaryButtonIcon,
  type AccountFlowStep,
} from './landing-account-copy'
import { maskPhone } from './landing-account-format'
import styles from './LandingAccountPage.module.css'

type LandingAccountFormProps = {
  flowStep: AccountFlowStep
  isLoading: boolean
  isSubmitting: boolean
  authError: string | null
  authSuccess: string | null
  widgetError: string | null
  phone: string
  loginPassword: string
  accountPassword: string
  accountPasswordConfirm: string
  registerName: string
  registerCpf: string
  registerEmail: string
  resetEmail: string
  salesOpen: boolean
  showPurchaseWidget: boolean
  primaryDisabled: boolean
  comprarDisabled: boolean
  isWidgetLoading: boolean
  salesClosedNotice: ReactNode
  onPhoneChange: (value: string) => void
  onLoginPasswordChange: (value: string) => void
  onAccountPasswordChange: (value: string) => void
  onAccountPasswordConfirmChange: (value: string) => void
  onRegisterNameChange: (value: string) => void
  onRegisterCpfChange: (value: string) => void
  onRegisterEmailChange: (value: string) => void
  onSubmit: () => void
  onForgotPassword: () => void
  onBackToPassword: () => void
  onBuy: () => void
}

function Field({
  id,
  label,
  icon,
  children,
}: {
  id: string
  label: string
  icon: string
  children: ReactNode
}) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.control}>
        <Icon icon={icon} className={styles.controlIcon} aria-hidden />
        {children}
      </div>
    </div>
  )
}

function FormIntro({
  title,
  description,
  notice,
}: {
  title: string
  description: string
  notice: string | null
}) {
  return (
    <header className={styles.formIntro}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <p className={styles.sectionDescription}>{description}</p>
      {notice ? <p className={styles.notice}>{notice}</p> : null}
    </header>
  )
}

export function LandingAccountForm({
  flowStep,
  isLoading,
  isSubmitting,
  authError,
  authSuccess,
  widgetError,
  phone,
  loginPassword,
  accountPassword,
  accountPasswordConfirm,
  registerName,
  registerCpf,
  registerEmail,
  resetEmail,
  salesOpen,
  showPurchaseWidget,
  primaryDisabled,
  comprarDisabled,
  isWidgetLoading,
  salesClosedNotice,
  onPhoneChange,
  onLoginPasswordChange,
  onAccountPasswordChange,
  onAccountPasswordConfirmChange,
  onRegisterNameChange,
  onRegisterCpfChange,
  onRegisterEmailChange,
  onSubmit,
  onForgotPassword,
  onBackToPassword,
  onBuy,
}: LandingAccountFormProps) {
  const flowContent = getAccountFlowContent(flowStep)
  const primaryIcon = getAccountPrimaryButtonIcon(flowStep, isSubmitting)
  const buyIcon = isWidgetLoading ? 'mdi:loading' : 'mdi:cart-plus'

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isLoading || primaryDisabled) {
      return
    }
    onSubmit()
  }

  if (isLoading) {
    return (
      <div className={styles.formBlock}>
        <FormIntro title="Entrar" description="Carregando dados da campanha..." notice={null} />
        {salesClosedNotice}
      </div>
    )
  }

  return (
    <form className={styles.formBlock} onSubmit={handleSubmit} noValidate>
      <FormIntro
        title={flowContent.title}
        description={flowContent.description}
        notice={flowContent.notice}
      />

      <div className={styles.formFields}>
        {flowStep === 'phone' ? (
          <Field id="landing-account-phone" label="Telefone" icon="mdi:cellphone">
            <input
              id="landing-account-phone"
              type="tel"
              className={styles.input}
              value={phone}
              onChange={(event) => onPhoneChange(maskPhone(event.target.value))}
              inputMode="tel"
              autoComplete="tel"
              placeholder="(00) 00000-0000"
              required
            />
          </Field>
        ) : null}

        {flowStep === 'password' ? (
          <>
            <Field id="landing-account-phone-readonly" label="Telefone" icon="mdi:cellphone">
              <input
                id="landing-account-phone-readonly"
                type="tel"
                className={`${styles.input} ${styles.readonlyInput}`}
                value={phone}
                readOnly
              />
            </Field>
            <Field id="landing-account-password" label="Senha" icon="mdi:lock-outline">
              <input
                id="landing-account-password"
                type="password"
                className={styles.input}
                value={loginPassword}
                onChange={(event) => onLoginPasswordChange(event.target.value)}
                autoComplete="current-password"
                placeholder="Digite sua senha"
                required
              />
            </Field>
            <button type="button" className={styles.forgotPasswordLink} onClick={onForgotPassword}>
              Esqueci minha senha
            </button>
          </>
        ) : null}

        {flowStep === 'forgot-password' ? (
          <>
            <Field id="landing-account-forgot-phone" label="Telefone" icon="mdi:cellphone">
              <input
                id="landing-account-forgot-phone"
                type="tel"
                className={`${styles.input} ${styles.readonlyInput}`}
                value={phone}
                readOnly
              />
            </Field>
            <button type="button" className={styles.textLinkButton} onClick={onBackToPassword}>
              Voltar ao login
            </button>
          </>
        ) : null}

        {flowStep === 'reset-password' ? (
          <>
            {resetEmail ? (
              <Field id="landing-account-reset-email" label="E-mail" icon="mdi:email-outline">
                <input
                  id="landing-account-reset-email"
                  type="email"
                  className={`${styles.input} ${styles.readonlyInput}`}
                  value={resetEmail}
                  readOnly
                />
              </Field>
            ) : null}
            <Field id="landing-account-reset-password" label="Nova senha" icon="mdi:lock-outline">
              <input
                id="landing-account-reset-password"
                type="password"
                className={styles.input}
                value={accountPassword}
                onChange={(event) => onAccountPasswordChange(event.target.value)}
                autoComplete="new-password"
                placeholder="Crie uma senha com pelo menos 8 caracteres"
                required
              />
            </Field>
            <Field
              id="landing-account-reset-password-confirm"
              label="Confirmar senha"
              icon="mdi:lock-check-outline"
            >
              <input
                id="landing-account-reset-password-confirm"
                type="password"
                className={styles.input}
                value={accountPasswordConfirm}
                onChange={(event) => onAccountPasswordConfirmChange(event.target.value)}
                autoComplete="new-password"
                placeholder="Repita a senha"
                required
              />
            </Field>
          </>
        ) : null}

        {flowStep === 'register' ? (
          <>
            <Field id="landing-account-register-phone" label="Telefone" icon="mdi:cellphone">
              <input
                id="landing-account-register-phone"
                type="tel"
                className={`${styles.input} ${styles.readonlyInput}`}
                value={phone}
                readOnly
              />
            </Field>
            <Field id="landing-account-register-email" label="E-mail" icon="mdi:email-outline">
              <input
                id="landing-account-register-email"
                type="email"
                className={styles.input}
                value={registerEmail}
                onChange={(event) => onRegisterEmailChange(event.target.value)}
                autoComplete="email"
                placeholder="voce@exemplo.com"
                required
              />
            </Field>
            <Field id="landing-account-register-name" label="Nome completo" icon="mdi:account-outline">
              <input
                id="landing-account-register-name"
                type="text"
                className={styles.input}
                value={registerName}
                onChange={(event) => onRegisterNameChange(event.target.value)}
                autoComplete="name"
                placeholder="Digite seu nome completo"
                required
              />
            </Field>
            <Field id="landing-account-register-cpf" label="CPF" icon="mdi:card-account-details-outline">
              <input
                id="landing-account-register-cpf"
                type="text"
                className={styles.input}
                value={registerCpf}
                onChange={(event) => onRegisterCpfChange(maskCpf(event.target.value))}
                inputMode="numeric"
                placeholder="000.000.000-00"
                required
              />
            </Field>
            <Field id="landing-account-register-password" label="Senha" icon="mdi:lock-outline">
              <input
                id="landing-account-register-password"
                type="password"
                className={styles.input}
                value={accountPassword}
                onChange={(event) => onAccountPasswordChange(event.target.value)}
                autoComplete="new-password"
                placeholder="Crie uma senha com pelo menos 8 caracteres"
                required
              />
            </Field>
            <Field
              id="landing-account-register-password-confirm"
              label="Confirmar senha"
              icon="mdi:lock-check-outline"
            >
              <input
                id="landing-account-register-password-confirm"
                type="password"
                className={styles.input}
                value={accountPasswordConfirm}
                onChange={(event) => onAccountPasswordConfirmChange(event.target.value)}
                autoComplete="new-password"
                placeholder="Repita a senha"
                required
              />
            </Field>
          </>
        ) : null}

        {authSuccess ? <p className={styles.success}>{authSuccess}</p> : null}
        {authError ? <p className={styles.error}>{authError}</p> : null}
        {widgetError ? <p className={styles.error}>{widgetError}</p> : null}

        <div className={styles.formActions}>
          <button type="submit" className={`${styles.submitButton} ${styles.buttonWithIcon}`} disabled={primaryDisabled}>
            <Icon
              icon={primaryIcon}
              width={18}
              height={18}
              className={isSubmitting ? styles.buttonIconSpin : undefined}
              aria-hidden
            />
            <span>{isSubmitting ? flowContent.loadingLabel : flowContent.buttonLabel}</span>
          </button>

          {showPurchaseWidget && salesOpen ? (
            <button
              type="button"
              className={styles.quietAction}
              onClick={onBuy}
              disabled={comprarDisabled}
            >
              <Icon
                icon={buyIcon}
                width={18}
                height={18}
                className={isWidgetLoading ? styles.buttonIconSpin : undefined}
                aria-hidden
              />
              <span>{isWidgetLoading ? 'Abrindo...' : 'Nova compra'}</span>
            </button>
          ) : null}

          {salesClosedNotice}
        </div>
      </div>
    </form>
  )
}
